// assets-panel — shared owner Assets (images/sounds) modal wiring (clientside-teacher-
// review, task 4.1). Extracted from editor-chrome so both the default editor surface
// and the student assignment surface can offer the same upload / list / remove flow
// over the existing endpoints (editor-api.uploadAsset + saveAssets). Controls are
// template-gated (owner AND features.assets), so this no-ops when they're absent.
//
//   wireAssets(ctx) — ctx = { core, payload, trinketId, editorApi, doc }
// Reuses POST /api/users/assets (upload) and PUT …/code (autosave); the server rebuilds
// the manifest, which is fed to core.setAssets so the NEXT Run fetches new media same-
// origin through the /runner-assets/ proxy (design D2/D3).

var IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

// Surfaces whose project is a student's course WORKING COPY (a `_creator`, no `_owner`): the
// owner-only `PUT …/code` 403s there, so the asset list goes to the dedicated working-assets route.
var WORKING_COPY_SURFACES = ['assignment', 'challenge', 'guided-make'];

/**
 * A copy of `editorApi` whose `saveAssets(id, files, assets)` PUTs ONLY `{ assets }` to
 * `PUT /api/projects/{id}/working-assets` and resolves `{ assetBaseUrl, assetManifest, assets }`.
 * Rejects with `err.status` on a non-OK reply. `fetchImpl` is a TEST SEAM.
 */
export function makeWorkingAssetsApi(editorApi, fetchImpl) {
  var api = {};
  var src = editorApi || {};
  Object.keys(src).forEach(function (k) { api[k] = src[k]; });
  api.isWorkingAssetsApi = true;
  api.saveAssets = function (id, files, assets) {
    var f = typeof fetchImpl === 'function' ? fetchImpl : (typeof fetch === 'function' ? fetch : null);
    if (!f) { return Promise.reject(new Error('assets-panel: fetch is not available')); }
    return f('/api/projects/' + encodeURIComponent(id) + '/working-assets', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assets: Array.isArray(assets) ? assets : [] })
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('assets-panel: working-assets save failed (' + res.status + ')');
        err.status = res.status;
        throw err;
      }
      return res.json();
    });
  };
  return api;
}

export function wireAssets(ctx) {
  var document = ctx.doc;
  var core = ctx.core;
  var editorApi = ctx.editorApi;
  var surface = document.body && document.body.getAttribute ? document.body.getAttribute('data-surface') : '';
  if (editorApi && !editorApi.isWorkingAssetsApi && WORKING_COPY_SURFACES.indexOf(surface) !== -1) {
    editorApi = makeWorkingAssetsApi(editorApi);
  }
  var trinketId = ctx.trinketId;
  var payload = ctx.payload || {};

  var assetsBtn = document.getElementById('runner-assets');
  var assetsModal = document.getElementById('assets-modal');
  var assetsList = document.getElementById('assets-list');
  var assetsFile = document.getElementById('assets-file');
  var assetsUpload = document.getElementById('assets-upload');
  var assetsStatus = document.getElementById('assets-status');
  var assetsClose = document.getElementById('assets-close');

  if (!assetsBtn || !assetsModal) { return; }

  // Working copy of the attached asset list, seeded from the load payload (D4).
  var assets = Array.isArray(payload.assets) ? payload.assets.slice() : [];

  function setAssetsStatus(text, isError) {
    if (!assetsStatus) { return; }
    assetsStatus.textContent = text || '';
    assetsStatus.classList.toggle('runner-error', !!isError);
  }

  function openModal(focusEl) {
    assetsModal.hidden = false;
    if (focusEl && focusEl.focus) { focusEl.focus(); }
  }
  function closeModal() { assetsModal.hidden = true; }

  // Click the backdrop (not the dialog) to dismiss; Esc closes too.
  assetsModal.addEventListener('click', function (e) { if (e.target === assetsModal) { closeModal(); } });
  assetsModal.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) { closeModal(); }
  });
  if (assetsClose) { assetsClose.addEventListener('click', closeModal); }

  function renderAssets() {
    if (!assetsList) { return; }
    while (assetsList.firstChild) { assetsList.removeChild(assetsList.firstChild); }
    if (!assets.length) {
      var empty = document.createElement('li');
      empty.className = 'assets-empty';
      empty.textContent = 'No images or sounds yet.';
      assetsList.appendChild(empty);
      return;
    }
    assets.forEach(function (asset) {
      var li = document.createElement('li');
      li.className = 'assets-item';
      // Thumbnail via the SAME-ORIGIN proxy (resolves by basename), never the
      // cross-origin store URL which require-corp would block.
      if (IMAGE_RE.test(asset.name || '')) {
        var img = document.createElement('img');
        img.className = 'assets-thumb';
        img.alt = '';
        img.src = editorApi.assetUrl(trinketId + '/' + asset.name);
        li.appendChild(img);
      } else {
        var icon = document.createElement('span');
        icon.className = 'assets-thumb assets-thumb-audio';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '♪';
        li.appendChild(icon);
      }
      var nameEl = document.createElement('span');
      nameEl.className = 'assets-name';
      nameEl.textContent = asset.name;
      li.appendChild(nameEl);
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'runner-btn assets-remove';
      rm.textContent = 'Remove';
      rm.addEventListener('click', function () { removeAsset(asset); });
      li.appendChild(rm);
      assetsList.appendChild(li);
    });
  }

  // Autosave the current files + the working asset list, then adopt the server's
  // rebuilt manifest (single source of truth) so the next Run stages the change.
  function persistAssets(doneText) {
    return editorApi.saveAssets(trinketId, core.getFiles(), assets).then(function (res) {
      core.setAssets({ assetBaseUrl: res.assetBaseUrl, assetManifest: res.assetManifest });
      if (Array.isArray(res.assets)) { assets = res.assets; } // server-normalised list
      renderAssets();
      setAssetsStatus(doneText || 'Saved', false);
      announceAssets(res.assetBaseUrl);
    });
  }

  // activity-download (Trello DNivkjIh): tell the page the asset list changed, so the Download
  // button includes a picture uploaded a moment ago. Built from the panel's OWN post-save list.
  // Guarded and swallowed: a missing document / CustomEvent must never turn a successful save
  // into the rollback path.
  function announceAssets(assetBaseUrl) {
    try {
      var view = document && document.defaultView;
      var Ctor = view && typeof view.CustomEvent === 'function' ? view.CustomEvent : null;
      if (!Ctor || typeof document.dispatchEvent !== 'function') { return; }
      document.dispatchEvent(new Ctor('pc-assets-changed', {
        detail: { assetBaseUrl: assetBaseUrl, assets: assets.slice() }
      }));
    } catch (err) { console.error(err); }
  }

  function removeAsset(asset) {
    assets = assets.filter(function (a) { return a !== asset && a.name !== asset.name; });
    renderAssets();
    setAssetsStatus('Saving…', false);
    persistAssets('Removed').catch(function (err) {
      assets = assets.concat([asset]); // roll back: nothing was removed server-side
      renderAssets();
      setAssetsStatus('Remove failed', true);
      console.error(err);
    });
  }

  assetsBtn.addEventListener('click', function () {
    setAssetsStatus('', false);
    renderAssets();
    openModal(assetsUpload);
  });

  if (assetsUpload && assetsFile) {
    // The visible button proxies the hidden native file input.
    assetsUpload.addEventListener('click', function () { assetsFile.click(); });
    assetsFile.addEventListener('change', function () {
      var file = assetsFile.files && assetsFile.files[0];
      if (!file) { return; }
      assetsUpload.disabled = true;
      setAssetsStatus('Uploading…', false);
      editorApi.uploadAsset(file)
        .then(function (uploaded) {
          var added = {
            id: uploaded.id, url: uploaded.url, name: uploaded.name, thumbnail: uploaded.thumb
          };
          assets.push(added);
          setAssetsStatus('Saving…', false);
          return persistAssets('Uploaded').catch(function (err) {
            // roll back: a failed save must not leave a ghost row for a file nothing stored
            assets = assets.filter(function (a) { return a !== added; });
            renderAssets();
            throw err;
          });
        })
        .catch(function (err) {
          var status = err && err.status;
          var msg = 'Upload failed.';
          if (status === 501) { msg = 'Media upload is not available.'; }
          else if (status === 413) { msg = 'That file is too large (5 MB max).'; }
          else if (status === 415) { msg = 'That file type is not supported.'; }
          setAssetsStatus(msg, true);
          console.error(err);
        })
        .then(function () {
          assetsUpload.disabled = false;
          if (assetsFile) { assetsFile.value = ''; } // allow re-selecting the same file
        });
    });
  }
}
